import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useEffect } from 'react';
import { CheckSquare, CheckCircle2, FileText, Eye, Edit2, Trash2 } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { hasWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
// Reuses the exact same tab the standalone Expenses screen shows under
// Approvals — expense approvals used to live only there, so an approver had
// to know to go check a second screen instead of seeing them here with
// everything else awaiting a decision.
import { ApprovalsTab as ExpenseApprovalsTab } from '@/features/accounts/components/AdvancedExpenseTabs';

// Import the comprehensive approval inbox styles
import '@/features/approvals/styles/approval-inbox.css';

export function ApprovalInboxPage() {
  const user = useUser();
  const isEmployee = user?.role === 'employee';
  // Stock and production queues are behind their own workspace modules; asking
  // for them without the module only 403s (Accounts holds neither), so skip.
  const canInventory = hasWorkspaceAccess(user, 'inventory');
  const canManufacturing = hasWorkspaceAccess(user, 'manufacturing');
  const [inbox, setInbox] = useState<any[]>([]);
  // Purchase Requisitions / Purchase Orders awaiting approval (Purchase CRM
  // audit) — previously these only surfaced inside the P2P screen itself;
  // an approver had to know to go look there rather than seeing them here
  // alongside every other pending approval.
  const [pendingRequisitions, setPendingRequisitions] = useState<any[]>([]);
  const [pendingPos, setPendingPos] = useState<any[]>([]);
  // Inventory Adjustments/Transfers/Stock Counts (final wave of the workflow
  // audit) — each already raised a real pending state with a real approve
  // endpoint; none of them ever showed up anywhere near the rest of what an
  // approver has to clear. Leave requests are deliberately NOT added here —
  // this inbox is gated to the accounts workspace and leave approval is
  // gated to HR/superadmin (a different workspace); unifying them would
  // either 403 for an accounts-only approver or broaden this page to HR
  // staff. Same call already made and explained earlier in this audit pass.
  const [pendingAdjustments, setPendingAdjustments] = useState<any[]>([]);
  const [pendingTransfers, setPendingTransfers] = useState<any[]>([]);
  const [pendingCounts, setPendingCounts] = useState<any[]>([]);
  const [pendingChallans, setPendingChallans] = useState<any[]>([]);
  const [pendingProductionOrders, setPendingProductionOrders] = useState<any[]>([]);
  // Legacy Journal Vouchers (accounts/journal-vouchers.js) — a separate
  // draft→submit→approve lifecycle from the Accounting V2 journals already
  // shown above. Both are fully accounts-workspace-scoped, so unlike Leave
  // Requests/POS variance/Sales Returns this one belongs here: an approver
  // who only checks this inbox would otherwise never see a V1 voucher
  // waiting in JournalVoucherList.tsx's separate screen.
  const [pendingJournalVouchers, setPendingJournalVouchers] = useState<any[]>([]);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  // Bulk-select state for Journals / POs (workflow audit — mirrors the
  // bulk-approve pattern already proven on Expenses, so an approver clearing
  // a backlog isn't stuck clicking Approve one row at a time). Each expense
  // is approved individually server-side, so one rejection never aborts the
  // rest of the batch.
  const [selectedJournals, setSelectedJournals] = useState<Set<string>>(new Set());
  const [selectedPos, setSelectedPos] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleIn = (set: Set<string>, setSet: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSet(next);
  };

  const fetchInbox = async () => {
    setLoading(true);

    try {
      const [jRes, prRes, poRes, adjRes, transRes, countRes, challanRes, mfgRes, jvRes] = await Promise.allSettled([
        api.get('/api/accounting/approvals/inbox'),
        api.get('/api/accounting/p2p/requisitions?status=submitted'),
        api.get('/api/accounting/p2p/pos?status=submitted'),
        canInventory ? api.get('/api/inventory/adjustments?status=submitted') : Promise.reject(new Error('no module access')),
        canInventory ? api.get('/api/inventory/transfers?status=submitted') : Promise.reject(new Error('no module access')),
        api.get('/api/accounting/inventory/counts?status=submitted'),
        api.get('/api/accounts/commercial-documents/delivery-challans?status=draft'),
        canManufacturing ? api.get('/api/manufacturing/orders?status=draft') : Promise.reject(new Error('no module access')),
        api.get('/api/accounts/journal-vouchers?status=pending_approval'),
      ]);

      if (jRes.status === 'fulfilled' && (jRes.value.data?.ok || jRes.value.data?.success)) {
        setInbox(Array.isArray(jRes.value.data.data) ? jRes.value.data.data : []);
      }
      if (prRes.status === 'fulfilled' && prRes.value.data?.ok) {
        setPendingRequisitions(prRes.value.data.data?.items || []);
      }
      if (poRes.status === 'fulfilled' && poRes.value.data?.ok) {
        setPendingPos(poRes.value.data.data?.items || []);
      }
      if (adjRes.status === 'fulfilled') {
        setPendingAdjustments(adjRes.value.data?.data || []);
      }
      if (transRes.status === 'fulfilled') {
        setPendingTransfers(transRes.value.data?.data || []);
      }
      if (countRes.status === 'fulfilled' && countRes.value.data?.ok) {
        setPendingCounts(countRes.value.data.data?.items || []);
      }
      if (challanRes.status === 'fulfilled') {
        const rows = Array.isArray(challanRes.value.data) ? challanRes.value.data : [];
        setPendingChallans(rows.filter((d: any) => d.approvalRequired && !d.approvedAt));
      }
      if (mfgRes.status === 'fulfilled') {
        setPendingProductionOrders(mfgRes.value.data?.orders || []);
      }
      if (jvRes.status === 'fulfilled') {
        const data = jvRes.value.data;
        setPendingJournalVouchers(Array.isArray(data) ? data : data?.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const approveRequisition = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/accounting/p2p/requisitions/${id}/approve`);
      setToast('Purchase requisition approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the requisition.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approvePo = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/accounting/p2p/pos/${id}/approve`);
      setToast('Purchase order approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the purchase order.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveAdjustment = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/inventory/adjustments/${id}/approve`);
      setToast('Stock adjustment approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the adjustment.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveTransfer = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/inventory/transfers/${id}/approve`);
      setToast('Stock transfer approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the transfer.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveCount = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/accounting/inventory/counts/${id}/approve`);
      setToast('Physical count approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the count.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveChallan = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/accounts/commercial-documents/dispatch/${id}/approval/approve`);
      setToast('Dispatch approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the dispatch.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveProductionOrder = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/manufacturing/orders/${id}/approve`);
      setToast('Production order released.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the production order.'));
    } finally {
      setApprovingId(null);
    }
  };

  const approveJournalVoucher = async (id: string) => {
    setApprovingId(id);
    try {
      await api.post(`/api/accounts/journal-vouchers/${id}/approve`);
      setToast('Journal voucher approved.');
      setTimeout(() => setToast(null), 3000);
      fetchInbox();
    } catch (e: any) {
      alert(getApiErrorMessage(e, 'Failed to approve the journal voucher.'));
    } finally {
      setApprovingId(null);
    }
  };

  const bulkApproveJournals = async () => {
    if (!selectedJournals.size) return;
    setBulkBusy(true);
    let ok = 0;
    const failures: string[] = [];
    for (const id of selectedJournals) {
      try {
        await api.post(`/api/accounting/journals/${id}/approve`, { segregationOfDuties: true });
        ok += 1;
      } catch (e: any) {
        failures.push(getApiErrorMessage(e, 'failed'));
      }
    }
    setSelectedJournals(new Set());
    setBulkBusy(false);
    setToast(`${ok} journal${ok === 1 ? '' : 's'} approved & posted.${failures.length ? ` ${failures.length} failed.` : ''}`);
    setTimeout(() => setToast(null), 3000);
    fetchInbox();
  };

  const bulkApprovePos = async () => {
    if (!selectedPos.size) return;
    setBulkBusy(true);
    let ok = 0;
    const failures: string[] = [];
    for (const id of selectedPos) {
      try {
        await api.post(`/api/accounting/p2p/pos/${id}/approve`);
        ok += 1;
      } catch (e: any) {
        failures.push(getApiErrorMessage(e, 'failed'));
      }
    }
    setSelectedPos(new Set());
    setBulkBusy(false);
    setToast(`${ok} purchase order${ok === 1 ? '' : 's'} approved.${failures.length ? ` ${failures.length} failed.` : ''}`);
    setTimeout(() => setToast(null), 3000);
    fetchInbox();
  };

  useEffect(() => {
    fetchInbox();
  }, []);

  const handleAction = async (id: string, action: 'approve' | 'post') => {
    try {
      const { data } = await api.post(`/api/accounting/journals/${id}/${action}`, {
        segregationOfDuties: true
      });
      if (data.ok || data.success) {
        setToast(`Journal entry successfully ${action}d!`);
        setTimeout(() => setToast(null), 3000);
        fetchInbox();
      } else {
        alert(data.error?.message || `Failed to ${action} journal.`);
      }
    } catch (err: any) {
      alert(getApiErrorMessage(err, `Failed to ${action} journal.`));
    }
  };

return (
    <div className="text-slate-900 dark:text-slate-100 space-y-6">
      {toast && (
        <div className="approval-toast" role="alert" aria-live="polite">
          <CheckCircle2 className="toast-icon" />
          <span className="toast-message">{toast}</span>
        </div>
      )}

      {/* Page Header */}
      {/* The old header also carried a search box and Export / New Request
          buttons with no handlers; they did nothing, so they are not shown. */}
      <PageHeader
        icon={CheckSquare}
        eyebrow="Finance"
        title="Approval Inbox"
        description="Everything waiting for a decision — journals, purchase requests and orders, stock movements, challans, production orders and vouchers — with maker-checker guards."
      />

      <div className="space-y-8">
        {/* JOURNALS TABLE */}
        <section aria-labelledby="journal-entries-heading">
          <h2 id="journal-entries-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Journal Entries</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading journals...
              </div>
            ) : inbox.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No journal entries pending</p>
                <p className="empty-description">There are no pending journal entries in the approval queue.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 mb-3">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={inbox.length > 0 && inbox.every((j) => selectedJournals.has(j._id))}
                      onChange={(e) => setSelectedJournals(e.target.checked ? new Set(inbox.map((j) => j._id)) : new Set())}
                      aria-label="Select all pending journals"
                    />
                    {selectedJournals.size > 0 ? `${selectedJournals.size} selected` : 'Select all'}
                  </label>
                  {selectedJournals.size > 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={bulkApproveJournals}
                        disabled={bulkBusy}
                        className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                      >
                        Approve & Post {selectedJournals.size}
                      </button>
                      <button onClick={() => setSelectedJournals(new Set())} className="approval-toolbar-btn approval-toolbar-btn--sm">
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="w-10"></th>
                      <th scope="col" className="approval-cell-id">Journal No</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Narration & Reference</th>
                      <th scope="col" className="text-right approval-cell-amount">Amount</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inbox.map((j) => (
                      <tr key={j._id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedJournals.has(j._id)}
                            onChange={() => toggleIn(selectedJournals, setSelectedJournals, j._id)}
                            aria-label={`Select ${j.journalNo || 'draft journal'}`}
                          />
                        </td>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{j.journalNo || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">
                          {new Date(j.journalDate).toLocaleDateString('en-IN')}
                        </td>
                        <td className="approval-cell-client">
                          {j.narration}
                        </td>
                        <td className="text-right approval-cell-amount">
                          {formatINR(j.totalDebit / 100)}
                        </td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => handleAction(j._id, 'approve')}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve and post ${j.journalNo}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve & Post</span>
                            </button>
                            
                            <div className="approval-action-icons">
                              <button
                                className="approval-action-btn"
                                aria-label={`View ${j.journalNo}`}
                              >
                                <Eye className="action-icon" aria-hidden="true" />
                              </button>
                              <button
                                className="approval-action-btn"
                                aria-label={`Edit ${j.journalNo}`}
                              >
                                <Edit2 className="action-icon" aria-hidden="true" />
                              </button>
                              <button
                                className="approval-action-btn delete"
                                aria-label={`Delete ${j.journalNo}`}
                              >
                                <Trash2 className="action-icon" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* EXPENSE APPROVALS (Workflow audit — folded in from the standalone Expenses screen) */}
        <section aria-labelledby="expense-approvals-heading">
          <h2 id="expense-approvals-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Expense Approvals</h2>
          <div className="approval-table-container p-4">
            {isEmployee ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">Approvals restricted</p>
                <p className="empty-description">Approval management is restricted to administrators.</p>
              </div>
            ) : (
              <ExpenseApprovalsTab />
            )}
          </div>
        </section>

        {/* PURCHASE REQUISITIONS TABLE (Purchase CRM audit) */}
        <section aria-labelledby="purchase-requisitions-heading">
          <h2 id="purchase-requisitions-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Purchase Requisitions</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading requisitions...
              </div>
            ) : pendingRequisitions.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No purchase requisitions pending</p>
                <p className="empty-description">There are no submitted requisitions awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">PR Number</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Department</th>
                      <th scope="col">Priority</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequisitions.map((pr) => (
                      <tr key={pr._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{pr.prNumber || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">{new Date(pr.date).toLocaleDateString('en-IN')}</td>
                        <td className="approval-cell-client">{pr.departmentId || pr.department || '—'}</td>
                        <td className="approval-cell-client capitalize">{pr.priority || 'normal'}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveRequisition(pr._id)}
                              disabled={approvingId === pr._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve ${pr.prNumber}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* PURCHASE ORDERS TABLE (Purchase CRM audit) */}
        <section aria-labelledby="purchase-orders-heading">
          <h2 id="purchase-orders-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Purchase Orders</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading purchase orders...
              </div>
            ) : pendingPos.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No purchase orders pending</p>
                <p className="empty-description">There are no submitted purchase orders awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 mb-3">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={pendingPos.length > 0 && pendingPos.every((po) => selectedPos.has(po._id))}
                      onChange={(e) => setSelectedPos(e.target.checked ? new Set(pendingPos.map((po) => po._id)) : new Set())}
                      aria-label="Select all pending purchase orders"
                    />
                    {selectedPos.size > 0 ? `${selectedPos.size} selected` : 'Select all'}
                  </label>
                  {selectedPos.size > 0 && (
                    <div className="flex gap-2">
                      <button
                        onClick={bulkApprovePos}
                        disabled={bulkBusy}
                        className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                      >
                        Approve {selectedPos.size}
                      </button>
                      <button onClick={() => setSelectedPos(new Set())} className="approval-toolbar-btn approval-toolbar-btn--sm">
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="w-10"></th>
                      <th scope="col" className="approval-cell-id">PO Number</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Supplier</th>
                      <th scope="col" className="text-right approval-cell-amount">Amount</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPos.map((po) => (
                      <tr key={po._id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPos.has(po._id)}
                            onChange={() => toggleIn(selectedPos, setSelectedPos, po._id)}
                            aria-label={`Select ${po.poNumber || 'draft PO'}`}
                          />
                        </td>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{po.poNumber || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">{new Date(po.date).toLocaleDateString('en-IN')}</td>
                        <td className="approval-cell-client">{po.supplierName || '—'}</td>
                        <td className="text-right approval-cell-amount">{formatINR((po.grandTotalMinor || 0) / 100)}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approvePo(po._id)}
                              disabled={approvingId === po._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve ${po.poNumber}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* INVENTORY ADJUSTMENTS TABLE (workflow audit — final wave) */}
        <section aria-labelledby="adjustments-heading">
          <h2 id="adjustments-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Inventory Adjustments</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading adjustments...
              </div>
            ) : pendingAdjustments.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No stock adjustments pending</p>
                <p className="empty-description">There are no submitted stock adjustments awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Reference</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Type</th>
                      <th scope="col">Location</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingAdjustments.map((a) => (
                      <tr key={a._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{a.referenceNumber || a._id.slice(-6)}</span>
                        </td>
                        <td className="approval-cell-date">{new Date(a.date).toLocaleDateString('en-IN')}</td>
                        <td className="approval-cell-client capitalize">{a.adjustmentType || 'normal'}</td>
                        <td className="approval-cell-client">{a.locationId?.name || '—'}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveAdjustment(a._id)}
                              disabled={approvingId === a._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve adjustment ${a.referenceNumber || a._id}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* INVENTORY TRANSFERS TABLE */}
        <section aria-labelledby="transfers-heading">
          <h2 id="transfers-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Inventory Transfers</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading transfers...
              </div>
            ) : pendingTransfers.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No stock transfers pending</p>
                <p className="empty-description">There are no submitted stock transfers awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Reference</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">From</th>
                      <th scope="col">To</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingTransfers.map((t) => (
                      <tr key={t._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{t.transferNumber || t._id.slice(-6)}</span>
                        </td>
                        <td className="approval-cell-date">{new Date(t.date).toLocaleDateString('en-IN')}</td>
                        <td className="approval-cell-client">{t.sourceLocationId?.name || '—'}</td>
                        <td className="approval-cell-client">{t.destinationLocationId?.name || '—'}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveTransfer(t._id)}
                              disabled={approvingId === t._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve transfer ${t.transferNumber || t._id}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* PHYSICAL STOCK COUNTS TABLE */}
        <section aria-labelledby="counts-heading">
          <h2 id="counts-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Physical Stock Counts</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading stock counts...
              </div>
            ) : pendingCounts.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No stock counts pending</p>
                <p className="empty-description">There are no submitted physical counts awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Count #</th>
                      <th scope="col" className="approval-cell-date">Snapshot Date</th>
                      <th scope="col">Type</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingCounts.map((c) => (
                      <tr key={c._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{c.countNo || c._id.slice(-6)}</span>
                        </td>
                        <td className="approval-cell-date">{c.snapshotDate ? new Date(c.snapshotDate).toLocaleDateString('en-IN') : '—'}</td>
                        <td className="approval-cell-client capitalize">{c.countType || 'full'}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveCount(c._id)}
                              disabled={approvingId === c._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve count ${c.countNo || c._id}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
        {/* DISPATCH APPROVALS TABLE */}
        <section aria-labelledby="dispatch-heading">
          <h2 id="dispatch-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Dispatch Approvals</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading dispatch requests...
              </div>
            ) : pendingChallans.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No dispatch approvals pending</p>
                <p className="empty-description">Every delivery challan flagged for a credit or stock-override sign-off has been cleared. Challans raised from Dispatch appear here as soon as they are saved as draft.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Challan #</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Customer</th>
                      <th scope="col">Reason</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingChallans.map((d) => (
                      <tr key={d._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{d.documentNumber || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">{new Date(d.date).toLocaleDateString('en-IN')}</td>
                        <td className="approval-cell-client">{d.customerId?.name || '—'}</td>
                        <td className="approval-cell-client">{d.approvalRequestNote || '—'}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveChallan(d._id)}
                              disabled={approvingId === d._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve dispatch for ${d.documentNumber}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* PRODUCTION ORDER APPROVALS TABLE */}
        <section aria-labelledby="production-orders-heading">
          <h2 id="production-orders-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Production Order Approvals</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading production orders...
              </div>
            ) : pendingProductionOrders.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No production orders pending</p>
                <p className="empty-description">There are no draft production orders awaiting release.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Order #</th>
                      <th scope="col" className="approval-cell-date">Scheduled</th>
                      <th scope="col" className="text-right approval-cell-amount">Target Qty</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingProductionOrders.map((o) => (
                      <tr key={o._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{o.orderNumber || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">
                          {o.scheduledStart ? new Date(o.scheduledStart).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td className="text-right approval-cell-amount">
                          {(o.targetQty || 0).toLocaleString('en-IN')} {o.targetUnit}
                        </td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveProductionOrder(o._id)}
                              disabled={approvingId === o._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Release production order ${o.orderNumber}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Release</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section aria-labelledby="journal-vouchers-heading">
          <h2 id="journal-vouchers-heading" className="mb-3 text-headline-sm font-semibold text-on-surface">Journal Vouchers (Legacy)</h2>
          <div className="approval-table-container">
            {loading ? (
              <div className="approval-loading" role="status" aria-busy="true">
                <div className="spinner" aria-hidden="true" />
                Loading journal vouchers...
              </div>
            ) : pendingJournalVouchers.length === 0 ? (
              <div className="approval-empty-state" role="status">
                <FileText className="empty-icon" aria-hidden="true" />
                <p className="empty-title">No journal vouchers pending</p>
                <p className="empty-description">There are no legacy journal vouchers awaiting approval.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="approval-table" role="table">
                  <thead>
                    <tr>
                      <th scope="col" className="approval-cell-id">Voucher #</th>
                      <th scope="col" className="approval-cell-date">Date</th>
                      <th scope="col">Description</th>
                      <th scope="col" className="text-right approval-cell-amount">Amount</th>
                      <th scope="col" className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingJournalVouchers.map((jv) => (
                      <tr key={jv._id}>
                        <td className="approval-cell-id">
                          <FileText className="id-icon w-4 h-4 text-slate-400" aria-hidden="true" />
                          <span>{jv.voucherNumber || 'Draft'}</span>
                        </td>
                        <td className="approval-cell-date">
                          {jv.date ? new Date(jv.date).toLocaleDateString('en-IN') : '—'}
                        </td>
                        <td>{jv.description || '—'}</td>
                        <td className="text-right approval-cell-amount">{formatINR(jv.totalAmount || 0)}</td>
                        <td className="text-right">
                          <div className="approval-actions approval-gap-2">
                            <button
                              onClick={() => approveJournalVoucher(jv._id)}
                              disabled={approvingId === jv._id}
                              className="approval-toolbar-btn primary approval-toolbar-btn--sm"
                              aria-label={`Approve journal voucher ${jv.voucherNumber}`}
                            >
                              <CheckCircle2 className="btn-icon w-4 h-4" />
                              <span>Approve</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default ApprovalInboxPage;
